import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Use relative path for Cloudflare Pages Functions
const API_URL = '/api';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const serviceOptions = [
  "Pool Tile Calcium Removal",
  "Stone & Surface Restoration",
  "Metal Blasting",
  "Commercial/Industrial Project",
  "Custom Stencil Etching",
  "Other",
];

const propertyTypes = ["Residential", "Commercial", "Industrial"];
const timelineOptions = ["ASAP", "Within 2 weeks", "Within 1 month", "Flexible"];
const howHeardOptions = ["Google Search", "Referral", "Social Media", "Drove By", "Other"];

const Quote = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    property_type: "",
    project_description: "",
    timeline: "",
    how_heard: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsSubmitted(true);
        toast.success(data.message);
      } else {
        toast.error(data.message || "Failed to submit quote. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="quote-page" className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg px-4"
        >
          <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-accent" />
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Quote Request Received
          </h2>
          <p className="text-text-secondary mb-8">
            Thank you for reaching out. We'll review your project details and get back to you within 24 hours.
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: "",
                email: "",
                phone: "",
                service_type: "",
                property_type: "",
                project_description: "",
                timeline: "",
                how_heard: "",
              });
            }}
            data-testid="submit-another-btn"
            className="btn-secondary"
          >
            Submit Another Request
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-surface">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } },
            }}
            className="max-w-4xl"
          >
            <motion.span variants={fadeUp} transition={{ duration: 0.6 }} className="label-accent block mb-6">
              Get Started
            </motion.span>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading font-bold text-5xl sm:text-6xl md:text-7xl text-text-primary leading-[0.95] tracking-tight mb-8"
            >
              Request a
              <br />
              <span className="text-text-secondary">Quote</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-text-secondary text-lg md:text-xl max-w-2xl leading-relaxed"
            >
              Tell us about your project. We'll respond with a detailed estimate within 24 hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-7"
            >
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label-accent block mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="quote-name-input"
                      className="input-custom"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="label-accent block mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="quote-email-input"
                      className="input-custom"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="label-accent block mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="quote-phone-input"
                    className="input-custom"
                    placeholder="(512) 988-8629"
                  />
                </div>

                {/* Service Type */}
                <div>
                  <label className="label-accent block mb-4">Service Type *</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {serviceOptions.map((service) => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, service_type: service }))}
                        data-testid={`service-option-${service.toLowerCase().replace(/ /g, '-')}`}
                        className={`p-4 text-left text-sm border transition-all duration-300 ${
                          formData.service_type === service
                            ? "border-accent bg-accent/10 text-text-primary"
                            : "border-white/10 text-text-secondary hover:border-white/30"
                        }`}
                      >
                        {service}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Property Type */}
                <div>
                  <label className="label-accent block mb-4">Property Type</label>
                  <div className="flex flex-wrap gap-3">
                    {propertyTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, property_type: type }))}
                        data-testid={`property-type-${type.toLowerCase()}`}
                        className={`px-6 py-3 text-sm border transition-all duration-300 ${
                          formData.property_type === type
                            ? "border-accent bg-accent/10 text-text-primary"
                            : "border-white/10 text-text-secondary hover:border-white/30"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Project Description */}
                <div>
                  <label className="label-accent block mb-2">Project Description *</label>
                  <textarea
                    name="project_description"
                    value={formData.project_description}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="quote-description-input"
                    className="input-custom resize-none"
                    placeholder="Describe your project, surface type, size, and any specific concerns..."
                  />
                </div>

                {/* Timeline */}
                <div>
                  <label className="label-accent block mb-4">Timeline</label>
                  <div className="flex flex-wrap gap-3">
                    {timelineOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, timeline: option }))}
                        data-testid={`timeline-${option.toLowerCase().replace(/ /g, '-')}`}
                        className={`px-6 py-3 text-sm border transition-all duration-300 ${
                          formData.timeline === option
                            ? "border-accent bg-accent/10 text-text-primary"
                            : "border-white/10 text-text-secondary hover:border-white/30"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {/* How Heard */}
                <div>
                  <label className="label-accent block mb-2">How did you hear about us?</label>
                  <select
                    name="how_heard"
                    value={formData.how_heard}
                    onChange={handleChange}
                    data-testid="quote-how-heard-select"
                    className="input-custom appearance-none cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    {howHeardOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.project_description}
                  data-testid="quote-submit-btn"
                  className="btn-primary w-full md:w-auto flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Quote Request
                      <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-5"
            >
              <div className="bg-surface border border-white/5 p-8 sticky top-32">
                <h3 className="font-heading font-bold text-xl text-text-primary mb-6">
                  What happens next?
                </h3>
                <ol className="space-y-6">
                  {[
                    { step: "01", title: "We Review", desc: "Our team reviews your project details within 24 hours." },
                    { step: "02", title: "We Respond", desc: "You'll receive a detailed estimate via email or phone." },
                    { step: "03", title: "We Schedule", desc: "Once approved, we coordinate a convenient project date." },
                  ].map((item) => (
                    <li key={item.step} className="flex gap-4">
                      <span className="font-mono text-accent text-sm">{item.step}</span>
                      <div>
                        <h4 className="font-heading font-bold text-text-primary mb-1">{item.title}</h4>
                        <p className="text-text-secondary text-sm">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="border-t border-white/5 mt-8 pt-8">
                  <p className="text-text-secondary text-sm mb-4">
                    Need faster response? Call us directly:
                  </p>
                  <a
                    href="tel:+15129888629"
                    data-testid="quote-phone-link"
                    className="font-heading font-bold text-xl text-text-primary hover:text-accent transition-colors"
                  >
                    (512) 988-8629
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
